import React, { useCallback, useMemo, useRef, useState } from 'react'
import { Dialog } from 'primereact/dialog'
import { Button, Alert, Row, Col, Form } from 'react-bootstrap'
import { Tooltip } from 'primereact/tooltip'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
    faArrowLeft,
    faExclamationTriangle,
    faFileCsv,
    faCheckCircle,
    faTimesCircle,
    faCircleExclamation,
    faCloudArrowUp,
    faXmark,
    faHashtag,
    faCoins,
    faCalendarDays,
    faFileLines,
    faCreditCard,
    faCircleCheck,
} from '@fortawesome/free-solid-svg-icons'
import useAxiosPrivate from '../../../../hooks/useAxiosPrivate'
import { useToast } from '../../../common/toast/hooks/useToast'

export type ImportPaymentsDialogProps = {
    visible: boolean
    onHide: () => void
    onSuccess?: () => void
}

type ParsedData = {
    headers: string[]
    rows: string[][]
    payment_methods: { id: number; name: string }[]
    statuses: { id: number; name: string }[]
}

type ColumnMapping = {
    code: number | ''
    amount: number | ''
    date: number | ''
    reference: number | ''
    payment_method: number | ''
    status: number | ''
}

type PreviewRow = {
    code: string
    amount: number
    payment_method_id: number
    status_id: number
    _rowIndex: number
    reference?: string
    _isDuplicate?: boolean
    _hazards?: { rowIndex: number; type: string; message: string }[]
}

function normalizeAmount(value: string): number {
    if (!value || typeof value !== 'string') return 0
    const cleaned = value.replace(/\s/g, '').replace(/,/g, '')
    const num = parseFloat(cleaned)
    return Number.isFinite(num) ? num : 0
}

/** Normalize for comparison: lowercase, hyphens/underscores to spaces, collapse spaces. */
function normalizeForMatch(s: string): string {
    return (s ?? '')
        .trim()
        .toLowerCase()
        .replace(/[-_]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
}

function findIdByName(list: { id: number; name: string }[], cellValue: string): number | null {
    const normalized = normalizeForMatch(cellValue ?? '')
    if (!normalized) return null
    const found = list.find((x) => normalizeForMatch(x.name ?? '') === normalized)
    return found ? found.id : null
}

/** Returns unique values in the column that don't match any option by name, and the list of expected names. */
function getInvalidColumnValues(
    rows: string[][],
    colIndex: number,
    options: { id: number; name: string }[]
): { invalid: string[]; expected: string[] } {
    const expected = options.map((x) => x.name.trim()).filter(Boolean)
    const seen = new Set<string>()
    const invalid: string[] = []
    for (const row of rows) {
        const cell = row[colIndex] !== undefined ? String(row[colIndex]).trim() : ''
        if (!cell) continue
        if (seen.has(cell)) continue
        seen.add(cell)
        if (!findIdByName(options, cell)) invalid.push(cell)
    }
    return { invalid, expected }
}

const ImportPaymentsDialog: React.FC<ImportPaymentsDialogProps> = ({
    visible,
    onHide,
    onSuccess,
}) => {
    const axiosPrivate = useAxiosPrivate()
    const { triggerToast } = useToast()

    const [step, setStep] = useState<'upload' | 'mapping' | 'preview' | 'confirm'>('upload')
    const [file, setFile] = useState<File | null>(null)
    const [parseError, setParseError] = useState('')
    const [parsed, setParsed] = useState<ParsedData | null>(null)
    const [columnMapping, setColumnMapping] = useState<ColumnMapping>({
        code: '',
        amount: '',
        date: '',
        reference: '',
        payment_method: '',
        status: '',
    })
    const [defaultMethodId, setDefaultMethodId] = useState<number | ''>('')
    const [defaultStatusId, setDefaultStatusId] = useState<number | ''>('')
    /** Single value: "col-0" = use CSV column 0, "default-5" = use default id 5 */
    const [paymentMethodChoice, setPaymentMethodChoice] = useState<string>('')
    const [statusChoice, setStatusChoice] = useState<string>('')
    const [previewPayload, setPreviewPayload] = useState<{
        rows: PreviewRow[]
        duplicateCodes: string[]
        hazards: { rowIndex: number; type: string; message: string }[]
        summary: { total: number; duplicateCount: number; hazardCount: number; validCount: number }
    } | null>(null)
    const [confirmDialogVisible, setConfirmDialogVisible] = useState(false)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [confirmText, setConfirmText] = useState('')
    const [isDragging, setIsDragging] = useState(false)
    const [mappingErrors, setMappingErrors] = useState<string[]>([])
    const fileInputRef = useRef<HTMLInputElement>(null)

    const reset = useCallback(() => {
        setStep('upload')
        setFile(null)
        setParseError('')
        setParsed(null)
        setColumnMapping({ code: '', amount: '', date: '', reference: '', payment_method: '', status: '' })
        setDefaultMethodId('')
        setDefaultStatusId('')
        setPreviewPayload(null)
        setConfirmDialogVisible(false)
        setConfirmText('')
        setMappingErrors([])
        setPaymentMethodChoice('')
        setStatusChoice('')
    }, [])

    const handleHide = useCallback(() => {
        reset()
        onHide()
    }, [onHide, reset])

    const parseFile = useCallback(
        async (selected: File) => {
            setFile(selected)
            setParseError('')
            setIsSubmitting(true)
            try {
                const formData = new FormData()
                formData.append('file', selected)
                const res = await axiosPrivate.post('/finance/payments/import/parse', formData, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                })
                if (res.data?.success && res.data?.data) {
                    const d = res.data.data
                    setParsed({
                        headers: d.headers || [],
                        rows: d.rows || [],
                        payment_methods: d.payment_methods || [],
                        statuses: d.statuses || [],
                    })
                    setStep('mapping')
                    if ((d.payment_methods?.length ?? 0) > 0) {
                        const firstId = d.payment_methods[0].id
                        setDefaultMethodId(firstId)
                        setPaymentMethodChoice(`default-${firstId}`)
                    }
                    if ((d.statuses?.length ?? 0) > 0) {
                        const firstId = d.statuses[0].id
                        setDefaultStatusId(firstId)
                        setStatusChoice(`default-${firstId}`)
                    }
                } else {
                    setParseError(res.data?.errors?.join(' ') || 'Failed to parse file')
                }
            } catch (err: any) {
                const msg =
                    err?.response?.data?.errors?.join(' ') ||
                    err?.message ||
                    'Failed to upload or parse file'
                setParseError(msg)
            } finally {
                setIsSubmitting(false)
            }
        },
        [axiosPrivate]
    )

    const handleFileSelect = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const selected = e.target.files?.[0]
            if (!selected) return
            parseFile(selected)
            e.target.value = ''
        },
        [parseFile]
    )

    const handleDrop = useCallback(
        (e: React.DragEvent) => {
            e.preventDefault()
            setIsDragging(false)
            if (isSubmitting) return
            const f = e.dataTransfer.files?.[0]
            if (f && (f.name.endsWith('.csv') || f.name.endsWith('.txt'))) parseFile(f)
        },
        [isSubmitting, parseFile]
    )

    const handleDragOver = useCallback((e: React.DragEvent) => {
        e.preventDefault()
        e.stopPropagation()
        if (!isSubmitting) setIsDragging(true)
    }, [isSubmitting])

    const handleDragLeave = useCallback((e: React.DragEvent) => {
        e.preventDefault()
        e.stopPropagation()
        setIsDragging(false)
    }, [])

    const handleBrowseClick = useCallback((e: React.MouseEvent) => {
        e.preventDefault()
        e.stopPropagation()
        if (!isSubmitting) fileInputRef.current?.click()
    }, [isSubmitting])

    const handleClearFile = useCallback((e: React.MouseEvent) => {
        e.preventDefault()
        e.stopPropagation()
        setFile(null)
        setParseError('')
        if (fileInputRef.current) fileInputRef.current.value = ''
    }, [])

    const buildMappedRows = useCallback((): PreviewRow[] => {
        if (!parsed) return []
        const fallbackMethodId = defaultMethodId !== '' ? Number(defaultMethodId) : (parsed.payment_methods[0]?.id ?? 0)
        const fallbackStatusId = defaultStatusId !== '' ? Number(defaultStatusId) : (parsed.statuses[0]?.id ?? 0)
        const codeCol = columnMapping.code === '' ? -1 : Number(columnMapping.code)
        const amountCol = columnMapping.amount === '' ? -1 : Number(columnMapping.amount)
        const dateCol = columnMapping.date === '' ? -1 : Number(columnMapping.date)
        const refCol = columnMapping.reference === '' ? -1 : Number(columnMapping.reference)
        const methodCol = columnMapping.payment_method === '' ? -1 : Number(columnMapping.payment_method)
        const statusCol = columnMapping.status === '' ? -1 : Number(columnMapping.status)

        return parsed.rows.map((row, i) => {
            const code = codeCol >= 0 && row[codeCol] !== undefined ? String(row[codeCol]).trim() : ''
            const amountRaw = amountCol >= 0 && row[amountCol] !== undefined ? String(row[amountCol]).trim() : ''
            const amount = normalizeAmount(amountRaw)
            const reference =
                refCol >= 0 && row[refCol] !== undefined ? String(row[refCol]).trim() : undefined
            const methodCell = methodCol >= 0 && row[methodCol] !== undefined ? String(row[methodCol]).trim() : ''
            const statusCell = statusCol >= 0 && row[statusCol] !== undefined ? String(row[statusCol]).trim() : ''
            const payment_method_id =
                methodCol >= 0 && methodCell
                    ? (findIdByName(parsed.payment_methods, methodCell) ?? fallbackMethodId)
                    : fallbackMethodId
            const status_id =
                statusCol >= 0 && statusCell
                    ? (findIdByName(parsed.statuses, statusCell) ?? fallbackStatusId)
                    : fallbackStatusId
            return {
                code,
                amount,
                payment_method_id,
                status_id,
                _rowIndex: i + 1,
                reference: reference || undefined,
            }
        })
    }, [parsed, columnMapping, defaultMethodId, defaultStatusId])

    const paymentMethodColumnError = useMemo(() => {
        if (!parsed?.rows?.length || !paymentMethodChoice.startsWith('col-')) return null
        const colIndex = parseInt(paymentMethodChoice.slice(4), 10)
        if (Number.isNaN(colIndex) || colIndex < 0) return null
        const { invalid, expected } = getInvalidColumnValues(parsed.rows, colIndex, parsed.payment_methods)
        if (invalid.length === 0) return null
        const columnLabel = parsed.headers?.[colIndex] || `Column ${colIndex + 1}`
        const sample = invalid.slice(0, 5).join(', ') + (invalid.length > 5 ? ` and ${invalid.length - 5} more` : '')
        return `"${columnLabel}" doesn't contain payment methods. Invalid values: ${sample}. Expected values like: ${expected.join(', ')}.`
    }, [parsed, paymentMethodChoice])

    const statusColumnError = useMemo(() => {
        if (!parsed?.rows?.length || !statusChoice.startsWith('col-')) return null
        const colIndex = parseInt(statusChoice.slice(4), 10)
        if (Number.isNaN(colIndex) || colIndex < 0) return null
        const { invalid, expected } = getInvalidColumnValues(parsed.rows, colIndex, parsed.statuses)
        if (invalid.length === 0) return null
        const columnLabel = parsed.headers?.[colIndex] || `Column ${colIndex + 1}`
        const sample = invalid.slice(0, 5).join(', ') + (invalid.length > 5 ? ` and ${invalid.length - 5} more` : '')
        return `"${columnLabel}" doesn't contain statuses. Invalid values: ${sample}. Expected values like: ${expected.join(', ')}.`
    }, [parsed, statusChoice])

    const handlePreview = useCallback(async () => {
        setMappingErrors([])
        setParseError('')
        const errors: string[] = []
        if (columnMapping.code === '') errors.push('Enter a unique payment code')
        if (columnMapping.amount === '') errors.push('Enter an amount')
        if (!paymentMethodChoice) errors.push('Select a payment method')
        if (!statusChoice) errors.push('Select payment status')
        if (paymentMethodColumnError) errors.push(paymentMethodColumnError)
        if (statusColumnError) errors.push(statusColumnError)
        if (errors.length > 0) {
            setMappingErrors(errors)
            return
        }
        const rows = buildMappedRows()
        if (rows.length === 0) {
            setMappingErrors(['No rows in file. Upload a CSV with data rows.'])
            return
        }
        const validRows = rows.filter((r) => (r.code?.trim() ?? '') !== '' && r.amount > 0)
        if (validRows.length === 0) {
            setMappingErrors(['No valid rows: each row needs a code and an amount greater than zero.'])
            return
        }
        setIsSubmitting(true)
        try {
            const res = await axiosPrivate.post('/finance/payments/import/preview', { rows })
            if (res.data?.success && res.data?.data) {
                setPreviewPayload(res.data.data)
                setStep('preview')
            } else {
                setParseError(res.data?.errors?.join(' ') || 'Preview failed')
            }
        } catch (err: any) {
            setParseError(err?.response?.data?.errors?.join(' ') || err?.message || 'Preview failed')
        } finally {
            setIsSubmitting(false)
        }
    }, [buildMappedRows, axiosPrivate, triggerToast, columnMapping, defaultMethodId, defaultStatusId, paymentMethodChoice, statusChoice, paymentMethodColumnError, statusColumnError])

    const handleConfirmImportClick = () => {
        setConfirmDialogVisible(true)
        setConfirmText('')
    }

    const handleConfirmImportSubmit = useCallback(async () => {
        if (!previewPayload?.rows?.length) return
        const rowsToSend = previewPayload.rows.map(({ _isDuplicate, _hazards, ...r }) => r)
        setIsSubmitting(true)
        try {
            const res = await axiosPrivate.post('/finance/payments/import/confirm', {
                rows: rowsToSend,
            })
            if (res.data?.success && res.data?.data) {
                const d = res.data.data
                triggerToast({
                    severity: 'success',
                    summary: 'Import complete',
                    detail: `${d.created} payment(s) created, ${d.skipped} skipped.${d.errors?.length ? ' Some errors: ' + d.errors.slice(0, 3).join('; ') : ''}`,
                    life: 8000,
                })
                setConfirmDialogVisible(false)
                handleHide()
                onSuccess?.()
            } else {
                triggerToast({
                    severity: 'error',
                    summary: 'Import failed',
                    detail: res.data?.errors?.join(' ') || 'Unknown error',
                })
            }
        } catch (err: any) {
            triggerToast({
                severity: 'error',
                summary: 'Import failed',
                detail: err?.response?.data?.errors?.join(' ') || err?.message || 'Request failed',
            })
        } finally {
            setIsSubmitting(false)
        }
    }, [previewPayload, axiosPrivate, triggerToast, handleHide, onSuccess])

    const canConfirmImport =
        previewPayload &&
        previewPayload.summary.validCount > 0 &&
        confirmText.trim().toUpperCase() === 'IMPORT'

    const handlePreviewRowChange = useCallback((rowIndex: number, field: 'payment_method_id' | 'status_id', value: number) => {
        setPreviewPayload((prev) =>
            prev
                ? {
                      ...prev,
                      rows: prev.rows.map((r) => (r._rowIndex === rowIndex ? { ...r, [field]: value } : r)),
                  }
                : null
        )
    }, [])

    const columnOptions = (parsed?.headers || []).map((h, i) => (
        <option key={i} value={i}>
            {h || `Column ${i + 1}`}
        </option>
    ))

    const mainDialogHeader = (
        <div className="import-modal__hero">
            <div className="import-modal__hero-bg" />
            <div className="import-modal__hero-content">
                <div className="import-modal__hero-icon">
                    <FontAwesomeIcon icon={faFileCsv} />
                </div>
                <div>
                    <h4 className="import-modal__hero-title">Import payments from CSV</h4>
                    <p className="import-modal__hero-subtitle">
                        Upload a bank or payment export, map columns, then review and confirm
                    </p>
                </div>
            </div>
        </div>
    )

    return (
        <>
            <Dialog
                className="import-modal"
                header={mainDialogHeader}
                visible={visible}
                onHide={handleHide}
                style={{ width: 'min(94vw, 920px)' }}
                breakpoints={{ '960px': '96vw' }}
                dismissableMask={false}
                closable={true}
                maximizable
                footer={
                    <div className="import-modal__footer">
                        {step === 'upload' ? (
                            <>
                                <Button
                                    variant="outline-secondary"
                                    onClick={handleHide}
                                    className="d-inline-flex align-items-center gap-2"
                                >
                                    Cancel
                                </Button>
                                {file && parsed && (
                                    <Button
                                        variant="primary"
                                        onClick={() => setStep('mapping')}
                                        className="d-inline-flex align-items-center gap-2"
                                    >
                                        <FontAwesomeIcon icon={faCheckCircle} />
                                        Next
                                    </Button>
                                )}
                            </>
                        ) : (
                            <>
                                <Button
                                    variant="outline-secondary"
                                    onClick={() => setStep(step === 'mapping' ? 'upload' : 'mapping')}
                                    className="d-inline-flex align-items-center gap-2"
                                >
                                    <FontAwesomeIcon icon={faArrowLeft} />
                                    Back
                                </Button>
                                {step === 'mapping' && (
                                    <Button
                                        variant="primary"
                                        onClick={handlePreview}
                                        disabled={isSubmitting}
                                        className="d-inline-flex align-items-center gap-2"
                                    >
                                        {isSubmitting ? (
                                            <>Loading…</>
                                        ) : (
                                            <>
                                                <FontAwesomeIcon icon={faCheckCircle} />
                                                Preview import
                                            </>
                                        )}
                                    </Button>
                                )}
                                {step === 'preview' && (
                                    <Button
                                        variant="warning"
                                        onClick={handleConfirmImportClick}
                                        className="d-inline-flex align-items-center gap-2"
                                    >
                                        <FontAwesomeIcon icon={faExclamationTriangle} />
                                        Confirm import
                                    </Button>
                                )}
                            </>
                        )}
                    </div>
                }
            >
                <div className="import-modal__body">
                    {parseError && (
                        <Alert variant="danger" dismissible onClose={() => setParseError('')}>
                            {parseError}
                        </Alert>
                    )}

                    {step === 'upload' && (
                    <div className="import-modal__config">
                        <div className="import-modal__config-header mb-3">
                            <h6>Upload file</h6>
                            <small className="text-muted">
                                CSV or TXT from your bank — you will map columns in the next step
                            </small>
                        </div>
                        <div
                            className={`import-drop-zone ${isDragging ? 'import-drop-zone--active' : ''} ${isSubmitting ? 'import-drop-zone--disabled' : ''}`}
                            onDrop={handleDrop}
                            onDragOver={handleDragOver}
                            onDragLeave={handleDragLeave}
                            onClick={file && !isSubmitting ? handleBrowseClick : undefined}
                        >
                            <input
                                id="import-payments-file-input"
                                ref={fileInputRef}
                                type="file"
                                accept=".csv,.txt"
                                onChange={handleFileSelect}
                                disabled={isSubmitting}
                                className="visually-hidden"
                                aria-label="Choose CSV file"
                            />
                            {!file ? (
                                <label
                                    htmlFor="import-payments-file-input"
                                    className="import-drop-zone__label"
                                >
                                    <div className="import-drop-zone__icon">
                                        <FontAwesomeIcon icon={faCloudArrowUp} />
                                    </div>
                                    <p className="import-drop-zone__title">Drag and drop your CSV here</p>
                                    <p className="import-drop-zone__hint">or click to browse — .csv and .txt only</p>
                                    <span className="import-drop-zone__browse">Choose file</span>
                                </label>
                            ) : (
                                <>
                                    <div className="import-drop-zone__icon">
                                        <FontAwesomeIcon icon={faFileCsv} />
                                    </div>
                                    <p className="import-drop-zone__title">{file.name}</p>
                                    <p className="import-drop-zone__hint">
                                        {isSubmitting ? 'Parsing file…' : 'Drop a new file or click to change'}
                                    </p>
                                    {!isSubmitting && (
                                        <div className="import-drop-zone__file" onClick={(e) => e.stopPropagation()}>
                                            <span>{file.name}</span>
                                            <button
                                                type="button"
                                                className="import-drop-zone__file-clear"
                                                onClick={handleClearFile}
                                                aria-label="Clear file"
                                            >
                                                <FontAwesomeIcon icon={faXmark} />
                                            </button>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                )}

                {step === 'mapping' && parsed && (
                    <div className="import-modal__config">
                        <div className="import-modal__config-header">
                            <h6>Column mapping</h6>
                            <small className="text-muted">Code and Amount are required</small>
                        </div>
                        {mappingErrors.length > 0 && (
                            <Alert variant="danger" dismissible onClose={() => setMappingErrors([])} className="mb-3">
                                <ul className="mb-0 ps-3">
                                    {mappingErrors.map((msg, i) => (
                                        <li key={i}>{msg}</li>
                                    ))}
                                </ul>
                            </Alert>
                        )}
                        <Row>
                            <Col lg={12}>
                                <div className="import-modal__mapping-card">
                                    <Row>
                                        <Col md={6}>
                                            <Form.Group className="mb-3">
                                                <Form.Label className="import-modal__field-label">
                                                    <span className="import-modal__field-icon" style={{ background: 'linear-gradient(135deg, #0a63ea, #6366f1)', color: 'white' }}>
                                                        <FontAwesomeIcon icon={faHashtag} />
                                                    </span>
                                                    Code / Reference column
                                                </Form.Label>
                                                <Form.Select
                                                    value={columnMapping.code === '' ? '' : String(columnMapping.code)}
                                                    onChange={(e) => {
                                                        setMappingErrors([])
                                                        setColumnMapping((m) => ({
                                                            ...m,
                                                            code: e.target.value === '' ? '' : Number(e.target.value),
                                                        }))
                                                    }}
                                                    isInvalid={mappingErrors.some((e) => e.includes('payment code'))}
                                                >
                                                    <option value="">— Select —</option>
                                                    {columnOptions}
                                                </Form.Select>
                                            </Form.Group>
                                            <Form.Group className="mb-3">
                                                <Form.Label className="import-modal__field-label">
                                                    <span className="import-modal__field-icon" style={{ background: 'linear-gradient(135deg, #059669, #10b981)', color: 'white' }}>
                                                        <FontAwesomeIcon icon={faCoins} />
                                                    </span>
                                                    Amount column
                                                </Form.Label>
                                                <Form.Select
                                                    value={columnMapping.amount === '' ? '' : String(columnMapping.amount)}
                                                    onChange={(e) => {
                                                        setMappingErrors([])
                                                        setColumnMapping((m) => ({
                                                            ...m,
                                                            amount: e.target.value === '' ? '' : Number(e.target.value),
                                                        }))
                                                    }}
                                                    isInvalid={mappingErrors.some((e) => e.includes('amount'))}
                                                >
                                                    <option value="">— Select —</option>
                                                    {columnOptions}
                                                </Form.Select>
                                            </Form.Group>
                                            <Form.Group className="mb-3">
                                                <Form.Label className="import-modal__field-label">
                                                    <span className="import-modal__field-icon" style={{ background: 'linear-gradient(135deg, #8b5cf6, #a78bfa)', color: 'white' }}>
                                                        <FontAwesomeIcon icon={faCreditCard} />
                                                    </span>
                                                    Payment method
                                                </Form.Label>
                                                <Form.Select
                                                    value={paymentMethodChoice}
                                                    onChange={(e) => {
                                                        setMappingErrors([])
                                                        const v = e.target.value
                                                        setPaymentMethodChoice(v)
                                                        if (v.startsWith('col-')) {
                                                            const col = parseInt(v.slice(4), 10)
                                                            setColumnMapping((m) => ({ ...m, payment_method: col }))
                                                            setDefaultMethodId(parsed?.payment_methods?.[0]?.id ?? '')
                                                        } else if (v.startsWith('default-')) {
                                                            setColumnMapping((m) => ({ ...m, payment_method: '' }))
                                                            setDefaultMethodId(parseInt(v.slice(8), 10))
                                                        }
                                                    }}
                                                    isInvalid={mappingErrors.some((e) => e.includes('payment method')) || !!paymentMethodColumnError}
                                                >
                                                    <option value="">— Select —</option>
                                                    {parsed?.headers?.map((h, i) => (
                                                        <option key={`col-${i}`} value={`col-${i}`}>
                                                            {h || `Column ${i + 1}`} (from CSV)
                                                        </option>
                                                    ))}
                                                    {parsed?.headers?.length ? <option disabled>—</option> : null}
                                                    {parsed?.payment_methods?.map((pm) => (
                                                        <option key={`default-${pm.id}`} value={`default-${pm.id}`}>
                                                            {pm.name}
                                                        </option>
                                                    ))}
                                                </Form.Select>
                                                {paymentMethodColumnError ? (
                                                    <Form.Text className="text-danger small d-block mt-1">{paymentMethodColumnError}</Form.Text>
                                                ) : (
                                                    <Form.Text className="text-muted small">Use a CSV column (match by name) or one value for all rows.</Form.Text>
                                                )}
                                            </Form.Group>
                                        </Col>
                                        <Col md={6}>
                                            <Form.Group className="mb-3">
                                                <Form.Label className="import-modal__field-label">
                                                    <span className="import-modal__field-icon" style={{ background: 'linear-gradient(135deg, #8b5cf6, #a78bfa)', color: 'white' }}>
                                                        <FontAwesomeIcon icon={faCalendarDays} />
                                                    </span>
                                                    Date column <span className="import-modal__label-optional">(optional)</span>
                                                </Form.Label>
                                                <Form.Select
                                                    value={columnMapping.date === '' ? '' : String(columnMapping.date)}
                                                    onChange={(e) =>
                                                        setColumnMapping((m) => ({
                                                            ...m,
                                                            date: e.target.value === '' ? '' : Number(e.target.value),
                                                        }))
                                                    }
                                                >
                                                    <option value="">— Skip —</option>
                                                    {columnOptions}
                                                </Form.Select>
                                            </Form.Group>
                                            <Form.Group className="mb-3">
                                                <Form.Label className="import-modal__field-label">
                                                    <span className="import-modal__field-icon" style={{ background: 'linear-gradient(135deg, #6366f1, #818cf8)', color: 'white' }}>
                                                        <FontAwesomeIcon icon={faFileLines} />
                                                    </span>
                                                    Reference / Description <span className="import-modal__label-optional">(optional)</span>
                                                </Form.Label>
                                                <Form.Select
                                                    value={columnMapping.reference === '' ? '' : String(columnMapping.reference)}
                                                    onChange={(e) =>
                                                        setColumnMapping((m) => ({
                                                            ...m,
                                                            reference: e.target.value === '' ? '' : Number(e.target.value),
                                                        }))
                                                    }
                                                >
                                                    <option value="">— Skip —</option>
                                                    {columnOptions}
                                                </Form.Select>
                                            </Form.Group>
                                            <Form.Group className="mb-3">
                                                <Form.Label className="import-modal__field-label">
                                                    <span className="import-modal__field-icon" style={{ background: 'linear-gradient(135deg, #059669, #10b981)', color: 'white' }}>
                                                        <FontAwesomeIcon icon={faCircleCheck} />
                                                    </span>
                                                    Status
                                                </Form.Label>
                                                <Form.Select
                                                    value={statusChoice}
                                                    onChange={(e) => {
                                                        setMappingErrors([])
                                                        const v = e.target.value
                                                        setStatusChoice(v)
                                                        if (v.startsWith('col-')) {
                                                            const col = parseInt(v.slice(4), 10)
                                                            setColumnMapping((m) => ({ ...m, status: col }))
                                                            setDefaultStatusId(parsed?.statuses?.[0]?.id ?? '')
                                                        } else if (v.startsWith('default-')) {
                                                            setColumnMapping((m) => ({ ...m, status: '' }))
                                                            setDefaultStatusId(parseInt(v.slice(8), 10))
                                                        }
                                                    }}
                                                    isInvalid={mappingErrors.some((e) => e.includes('payment status')) || !!statusColumnError}
                                                >
                                                    <option value="">— Select —</option>
                                                    {parsed?.headers?.map((h, i) => (
                                                        <option key={`col-${i}`} value={`col-${i}`}>
                                                            {h || `Column ${i + 1}`} (from CSV)
                                                        </option>
                                                    ))}
                                                    {parsed?.headers?.length ? <option disabled>—</option> : null}
                                                    {parsed?.statuses?.map((s) => (
                                                        <option key={`default-${s.id}`} value={`default-${s.id}`}>
                                                            {s.name}
                                                        </option>
                                                    ))}
                                                </Form.Select>
                                                {statusColumnError ? (
                                                    <Form.Text className="text-danger small d-block mt-1">{statusColumnError}</Form.Text>
                                                ) : (
                                                    <Form.Text className="text-muted small">Use a CSV column (match by name) or one value for all rows.</Form.Text>
                                                )}
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                </div>
                            </Col>
                        </Row>
                    </div>
                )}

                {step === 'preview' && previewPayload && (
                    <div className="import-modal__config">
                        <div className="import-modal__config-header mb-3">
                            <h6>Preview</h6>
                            <small className="text-muted">
                                {previewPayload.summary.validCount} to import
                                {previewPayload.summary.duplicateCount > 0 && ` · ${previewPayload.summary.duplicateCount} duplicate(s) skipped`}
                                {previewPayload.summary.hazardCount > 0 && ` · ${previewPayload.summary.hazardCount} hazard(s)`}
                            </small>
                        </div>
                        <Alert variant="info" className="mb-3 py-2">
                            <span className="text-success fw-semibold">{previewPayload.summary.validCount}</span> row(s) will be imported
                            {previewPayload.summary.duplicateCount > 0 && (
                                <> · <span className="text-warning">{previewPayload.summary.duplicateCount} duplicate(s) skipped</span></>
                            )}
                            {previewPayload.summary.hazardCount > 0 && (
                                <> · <span className="text-danger">{previewPayload.summary.hazardCount} hazard(s)</span></>
                            )}
                        </Alert>
                        <Tooltip target=".import-hazard-icon" position="left" />
                        <div className="import-preview-table-wrap table-responsive" style={{ maxHeight: '320px', overflow: 'auto' }}>
                            <table className="import-preview-table">
                                <thead>
                                    <tr>
                                        <th>#</th>
                                        <th>Code</th>
                                        <th>Amount</th>
                                        <th>Payment method</th>
                                        <th>Status</th>
                                        <th className="import-preview-col-result"></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {previewPayload.rows.slice(0, 100).map((row, idx) => {
                                        const isDup = row._isDuplicate
                                        const hasHazard =
                                            (row._hazards?.length ?? 0) > 0 ||
                                            (previewPayload.hazards?.some((h) => h.rowIndex === row._rowIndex) ?? false)
                                        const rowClass = isDup
                                            ? 'import-preview-row--duplicate'
                                            : hasHazard
                                            ? 'import-preview-row--hazard'
                                            : 'import-preview-row--ok'
                                        return (
                                            <tr key={idx} className={rowClass}>
                                                <td>{row._rowIndex}</td>
                                                <td>
                                                    {row.code || <em className="text-muted">empty</em>}
                                                    {isDup && (
                                                        <span className="ms-1 small text-warning" title="Duplicate">
                                                            <FontAwesomeIcon icon={faTimesCircle} />
                                                        </span>
                                                    )}
                                                </td>
                                                <td>{row.amount}</td>
                                                <td>
                                                    {parsed && (
                                                        <Form.Select
                                                            size="sm"
                                                            value={row.payment_method_id}
                                                            onChange={(e) =>
                                                                handlePreviewRowChange(row._rowIndex, 'payment_method_id', Number(e.target.value))
                                                            }
                                                            className="import-preview-select"
                                                        >
                                                            {parsed.payment_methods.map((pm) => (
                                                                <option key={pm.id} value={pm.id}>
                                                                    {pm.name}
                                                                </option>
                                                            ))}
                                                        </Form.Select>
                                                    )}
                                                </td>
                                                <td>
                                                    {parsed && (
                                                        <Form.Select
                                                            size="sm"
                                                            value={row.status_id}
                                                            onChange={(e) =>
                                                                handlePreviewRowChange(row._rowIndex, 'status_id', Number(e.target.value))
                                                            }
                                                            className="import-preview-select"
                                                        >
                                                            {parsed.statuses.map((s) => (
                                                                <option key={s.id} value={s.id}>
                                                                    {s.name}
                                                                </option>
                                                            ))}
                                                        </Form.Select>
                                                    )}
                                                </td>
                                                <td className="import-preview-col-result">
                                                    {hasHazard ? (
                                                        <span
                                                            className="import-hazard-icon small text-danger d-inline-block"
                                                            data-pr-tooltip={
                                                                row._hazards?.length
                                                                    ? row._hazards.map((h) => `• ${h.message}`).join('\n')
                                                                    : 'This row has validation issues.'
                                                            }
                                                            data-pr-position="left"
                                                        >
                                                            <FontAwesomeIcon icon={faCircleExclamation} />
                                                        </span>
                                                    ) : (
                                                        <span className="small text-success">
                                                            <FontAwesomeIcon icon={faCheckCircle} />
                                                        </span>
                                                    )}
                                                </td>
                                            </tr>
                                        )
                                    })}
                                </tbody>
                            </table>
                        </div>
                        {previewPayload.rows.length > 100 && (
                            <p className="small text-muted">Showing first 100 rows. All will be processed on confirm.</p>
                        )}
                    </div>
                )}
                </div>
            </Dialog>

            {/* Prominent confirmation dialog */}
            <Dialog
                className="import-confirm-modal"
                header={
                    <div className="import-modal__hero">
                        <div className="import-modal__hero-bg" />
                        <div className="import-modal__hero-content">
                            <div className="import-modal__hero-icon">
                                <FontAwesomeIcon icon={faExclamationTriangle} />
                            </div>
                            <div>
                                <h4 className="import-modal__hero-title">Confirm payment import</h4>
                                <p className="import-modal__hero-subtitle">
                                    This will add payments to the database. Duplicates will be skipped.
                                </p>
                            </div>
                        </div>
                    </div>
                }
                visible={confirmDialogVisible}
                onHide={() => !isSubmitting && setConfirmDialogVisible(false)}
                style={{ width: 'min(90vw, 480px)' }}
                modal
                closable={!isSubmitting}
                maximizable
                footer={
                    <div className="import-modal__footer">
                        <Button
                            variant="outline-secondary"
                            onClick={() => setConfirmDialogVisible(false)}
                            disabled={isSubmitting}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="danger"
                            onClick={handleConfirmImportSubmit}
                            disabled={!canConfirmImport || isSubmitting}
                            className="d-inline-flex align-items-center gap-2"
                        >
                            {isSubmitting ? (
                                <>Importing…</>
                            ) : (
                                <>
                                    <FontAwesomeIcon icon={faCheckCircle} />
                                    Yes, import payments
                                </>
                            )}
                        </Button>
                    </div>
                }
            >
                <Alert variant="warning" className="mb-3">
                    <strong>This action will add payments to the database.</strong>
                </Alert>
                {previewPayload && (
                    <ul className="mb-3">
                        <li><strong>{previewPayload.summary.validCount}</strong> payment(s) will be created</li>
                        {previewPayload.summary.duplicateCount > 0 && (
                            <li><strong>{previewPayload.summary.duplicateCount}</strong> duplicate(s) will be skipped</li>
                        )}
                        {previewPayload.summary.hazardCount > 0 && (
                            <li><strong>{previewPayload.summary.hazardCount}</strong> row(s) have warnings</li>
                        )}
                    </ul>
                )}
                <Form.Group>
                    <Form.Label>
                        Type <strong>IMPORT</strong> below to confirm:
                    </Form.Label>
                    <Form.Control
                        type="text"
                        value={confirmText}
                        onChange={(e) => setConfirmText(e.target.value)}
                        placeholder="IMPORT"
                        disabled={isSubmitting}
                        className={confirmText && confirmText.toUpperCase() !== 'IMPORT' ? 'border-warning' : ''}
                    />
                </Form.Group>
            </Dialog>
        </>
    )
}


export default ImportPaymentsDialog
